"use client";

import { useEffect, useState } from "react";
import { getCollectionByHandleApi } from "@/api/collection";
import { IApiProduct } from "@/types/collection";
import ProductCard from "@/components/ui/product-card";
import { useCart } from "@/contexts/cart-context";
import { ICartProductInfo } from "@/types/cart";
import { IAddToCartEvent, EventType, publishEvent } from "@/events";
import { convertPaisaToRupees } from "@/libs/utils";
import { checkInventory } from "@/api/inventory";

import HeadingSize from "@/components/ui/HeadingSize";

interface InventoryItem {
  skuCode: string;
  qty: number;
}

export default function FindYourAtomSection() {
  const [products, setProducts] = useState<IApiProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPackSizeIndex, setSelectedPackSizeIndex] = useState(0);
  const [inventoryMap, setInventoryMap] = useState<Record<string, number>>({});

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await getCollectionByHandleApi("ryze-gums");
        const productsData = response.products?.slice(0, 4) || [];
        setProducts(productsData);

        // Get all SKUs from all variants
        const allSkus = productsData.reduce((skus: string[], product) => {
          if (product.variant?.sku) {
            skus.push(product.variant.sku);
          }
          return skus;
        }, []);

        // Check inventory for all SKUs in one call
        if (allSkus.length > 0) {
          const inventoryResponse = await checkInventory(allSkus);
          const newInventoryMap = inventoryResponse.response.reduce(
            (acc: Record<string, number>, item: InventoryItem) => {
              acc[item.skuCode] = item.qty;
              return acc;
            },
            {}
          );
          setInventoryMap(newInventoryMap);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const { addToCart } = useCart();

  const handleAddToCart = (product: IApiProduct) => {
    if (!product) {
      console.error("Product not available");
      return;
    }

    // Make sure the product has variants
    if (!product.variant || product?.variant?.length === 0) {
      console.error("Product variants not available");
      return;
    }

    // Get the selected variant ID
    console.log("Product variants:", product.variant);
    const selectedVariant = product.variant;
    console.log("Selected variant:", selectedVariant);
    const variantId = selectedVariant?.id;
    if (!variantId) {
      console.error("Variant ID not available");
      return;
    }

    // Create minimal product info for display in cart
    const productInfo: ICartProductInfo = {
      title: product.title,
      price: String(selectedVariant?.price || product.price),
      image: product.image_url
        ? {
            id: product.id,
            url: product.image_url,
            alt_text: product.title,
            is_main: true,
            position: 1,
          }
        : undefined,
      slug: product.handle,
      variant: {
        id: variantId,
        title: selectedVariant?.title || product.title,
      },
    };

    const event: IAddToCartEvent = {
      type: EventType.ADD_TO_CART,
      productId: product.id,
      productName: product.title,
      price: convertPaisaToRupees(+selectedVariant?.price),
      currency: "INR",
      timestamp: Date.now(),
      variant: selectedVariant?.title || "",
    };

    publishEvent(event);

    // Add to cart with variant ID and product info
    addToCart(variantId, 1, productInfo);
  };

  return (
    <section className="bg-white py-4 sm:py-4 md:py-32 lg:pt-16 lg:pb-24">
      <div className="container-custom px-4 sm:px-6 max-w-7xl ">
        <div className="text-center mb-6 sm:mb-8 md:mb-8">
          <HeadingSize text="Find your Ryze" />
        </div>

        {isLoading ? (
          <div className="text-center py-4 sm:py-6 md:py-8 text-gray-500">
            Loading products...
          </div>
        ) : products.length > 0 ? (
          <div className="flex flex-row  overflow-x-auto   scrollbar-hide gap-5">
            {products.map((product) => (
              <div
                key={product.id}
                className="max-w-5xl mx-auto flex items-center justify-between gap-4"
              >
                <ProductCard
                  key={product.id}
                  title={product.title}
                  price={product?.variant?.price || "0"}
                  mrp={product?.variant?.compare_at_price || "0"}
                  image={product.image_url || "/placeholder-product.png"}
                  onAddToCart={() => handleAddToCart(product)}
                  handle={product.handle}
                  inStock={
                    product.variant?.sku
                      ? (inventoryMap[product.variant.sku] || 0) > 0
                      : false
                  }
                  isLoading={isLoading}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 sm:py-6 md:py-8 text-gray-500">
            No products found
          </div>
        )}
      </div>
    </section>
  );
}
